import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useStatsig } from './useStatsig';
import {
    closePreRegInviteNotificationPopUp,
    getPreRegInviteStatus,
    showInfoPopup,
    updatePreRegNotificationCanDisplayStatus,
} from '../../actions/notification-actions';
import { getEventDurationString } from '../../utils/events';

const usePreRegistrationNotification = (currentRoute) => {
    const notifications = useSelector(({ notifications }) => notifications);
    const splash = useSelector(({ splash }) => splash);
    const user = useSelector((state) => state.auth?.user);
    const dispatch = useDispatch();
    const history = useHistory();

    const { checkGate, featureGates } = useStatsig();
    const isPreRegInviteModalEnabled = checkGate(featureGates.PRE_REGISTRATION_INVITE_MODAL);

    const {
        preRegInviteStatus = {},
    } = notifications;

    useEffect(() => {
        if (user && !currentRoute?.notifications?.disablePreRegInvitation && isPreRegInviteModalEnabled) {
            dispatch(getPreRegInviteStatus(user?.id));
        }
    }, [user, currentRoute, isPreRegInviteModalEnabled]);

    const onConfirmClickPreRegInvite = useCallback(() => {
        history.push(`/events/${preRegInviteStatus.data.eventId}`);
        dispatch(closePreRegInviteNotificationPopUp());
    }, [preRegInviteStatus.data, history]);

    const onCancelClickPreRegInvite = useCallback(() => {
        dispatch(closePreRegInviteNotificationPopUp());
    }, []);

    useEffect(() => {
        if (currentRoute?.notifications?.useCustomTriggerToDisplayPreRegInvitation) {
            dispatch(updatePreRegNotificationCanDisplayStatus(false));
        } else {
            dispatch(updatePreRegNotificationCanDisplayStatus(true));
        }
        return () => {
            if (currentRoute?.notifications?.useCustomTriggerToDisplayPreRegInvitation) {
                dispatch(updatePreRegNotificationCanDisplayStatus(true));
            }
        };
    }, [currentRoute]);

    useEffect(() => {
        const shouldShowPreRegInvitePopup = !currentRoute?.notifications?.disablePreRegInvitation &&
            !splash.show &&
            preRegInviteStatus.canDisplayIfDataExists &&
            preRegInviteStatus.data;

        if (shouldShowPreRegInvitePopup) {
            dispatch(showInfoPopup({
                title: 'You have been invited to register.',
                message: (
                    <>
                        <div className="divider mt8 mb16" />
                        <div className="fw400 fs14">{preRegInviteStatus.data?.eventName}</div>
                        <div className="d-flex aic flex-wrap fs12 fw300 mt4">
                            <span>
                                {getEventDurationString(
                                    preRegInviteStatus.data?.eventStartDateLocalized,
                                    preRegInviteStatus.data?.eventEndDateLocalized,
                                )}
                            </span>
                            <span className="ml4 mr4">
                                •
                            </span>
                            <span>
                                {preRegInviteStatus.data?.location}
                            </span>
                        </div>
                        <ul className="fw400 fs14 mt8">
                            {preRegInviteStatus.data?.invitesDivisionCategories?.map((divisionCategory) => (
                                <li key={divisionCategory} className="mt4">
                                    {divisionCategory}
                                </li>
                            ))}
                        </ul>
                    </>
                ),
                noIcon: true,
                noOverlay: false,
                confirmButtonLabel: 'View Event',
                onConfirmClick: onConfirmClickPreRegInvite,
                showCancelButton: true,
                showConfirmButton: true,
                cancelButtonLabel: 'Close',
                onCancelClick: onCancelClickPreRegInvite,
                showCloseButton: false,
                showSupportLink: false,
            }));
        }
    }, [currentRoute, splash.show, preRegInviteStatus.data, preRegInviteStatus.canDisplayIfDataExists]);
};

export default usePreRegistrationNotification;
